import { Component, ComponentInterface, Element, h, Prop, Watch } from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
// eslint-disable-next-line import/extensions
import { DEFAULT_TRACKING_DELAY } from '../../../global/utility/tracking-helpers';
import { Trackable, TrackableContentId } from '../../../types/global';

export interface UiPage extends Trackable, TrackableContentId {
  title: string;
  url: string;
  hubPages?: UiHubPages;
  active?: boolean;
  description?: string;
  pages?: UiPage[];
}

export interface UiNavigationBody {
  site: UiPage;
}

export interface UiHubPages {
  title: string;
  pages: UiPage[];
}

@Component({
  tag: 'ui-cms-navigation',
  styleUrl: 'ui-cms-navigation.scss',
})
export class UiCmsNavigation implements ComponentInterface {
  @Element() el: HTMLUiCmsNavigationElement;

  navigation: HTMLUListElement;

  @Prop({ mutable: true }) body: UiNavigationBody;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  // SET in ui-cms-header, forwarded to ui-cms-navigation-element
  // allows default navigation on ui-cms-navigation-element click if TRUE
  // prevents default navigation on ui-cms-navigation-element click and emits triggerExternalNavigation event if FALSE
  @Prop({ attribute: 'navigateOnClick' }) navigateOnClick: boolean;

  @Prop({ mutable: true }) i18n = {
    navigationName: 'Hauptnavigation',
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  getFirstLevel(singlePage: UiPage, elementIndex: number) {
    return (
      <li>
        <ui-cms-navigation-element
          elementIndex={elementIndex}
          page={singlePage}
          onLinkClick={this.onLinkClick.bind(this)}
          navigateOnClick={this.navigateOnClick}
        ></ui-cms-navigation-element>
      </li>
    );
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
  }

  onLinkClick(event: CustomEvent<{ page: UiPage }>) {
    const { page } = event.detail;
    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: 'Click Navigation Element',
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Link',
        elementLabel: page.title,
        target: page.contentId,
      },
      category: {
        area: 'Navigation',
      },
    };

    if (trackingEvent) {
      setTimeout(() => {
        window.ui?.services?.tracking?.track(trackingEvent);
      }, DEFAULT_TRACKING_DELAY);
    }
  }

  render() {
    return (
      <ul
        class="main-navigation"
        aria-label={this.i18n.navigationName}
        ref={(el) => {
          this.navigation = el as HTMLUListElement;
        }}
      >
        {this.body?.site.pages?.map((singlePage, index) => this.getFirstLevel(singlePage, index))}
      </ul>
    );
  }
}
